import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ListChecks, Circle, CheckCircle2, Filter, Clock } from "lucide-react";
import { Avatar } from "@/components/Avatar";

const daysPending = (d) =>
  Math.max(0, Math.floor((Date.now() - new Date(`${d}T00:00:00`).getTime()) / 86400000));

function PendingBadge({ date }) {
  const n = daysPending(date);
  const cls =
    n >= 7
      ? "bg-destructive/10 text-destructive"
      : n >= 3
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
      : "bg-secondary text-muted-foreground";
  const label = n === 0 ? "today" : `${n}d`;
  return (
    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0 ${cls}`} data-testid={`pending-badge`}>
      <Clock className="h-3 w-3" /> {label}
    </span>
  );
}

export default function TeamPlanView() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [managerList, setManagerList] = useState([]);
  const [memberId, setMemberId] = useState("all");
  const [managerFilterId, setManagerFilterId] = useState("all");
  const [sortOrder, setSortOrder] = useState("overdue");

  useEffect(() => {
    api.get("/users").then((res) => {
      setMembers(res.data.filter((u) => u.role === "user"));
      setManagerList(res.data.filter((u) => u.role === "manager"));
    }).catch(() => {});
  }, []);

  const memberOptions = managerFilterId === "all"
    ? members
    : members.filter((m) => (m.manager_ids || []).includes(managerFilterId));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (managerFilterId !== "all") params.manager_id = managerFilterId;
      if (memberId !== "all") params.user_id = memberId;
      const { data } = await api.get("/reports/team-plans", { params });
      setGroups(data.groups || []);
    } catch (e) {
      console.error("Could not load team plans", e);
      toast.error("Could not load team plans");
    } finally {
      setLoading(false);
    }
  }, [memberId, managerFilterId]);

  useEffect(() => { load(); }, [load]);

  const processed = useMemo(() => {
    const gs = groups.map((g) => ({
      ...g,
      items: [...g.items].sort((a, b) =>
        sortOrder === "overdue" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)
      ),
    }));
    gs.sort((a, b) => {
      const ad = a.items[0]?.date || "";
      const bd = b.items[0]?.date || "";
      return sortOrder === "overdue" ? ad.localeCompare(bd) : bd.localeCompare(ad);
    });
    return gs;
  }, [groups, sortOrder]);

  const total = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <ListChecks className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold">Open plan items</h2>
          <p className="text-sm text-muted-foreground">
            Everything your team still has to do, so you can follow up.
          </p>
        </div>
        <Badge className="ml-auto rounded-full border-0 bg-primary/10 text-primary" data-testid="team-plans-count">
          {total} open
        </Badge>
      </div>

      {/* Filters */}
      <Card className="p-5 sm:p-6 rounded-xl mb-6">
        <div className="flex items-center gap-1.5 mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <div className="space-y-2">
            <Label>Sort by pending</Label>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger data-testid="plan-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="overdue" data-testid="sort-overdue">Longest pending first</SelectItem>
                <SelectItem value="recent" data-testid="sort-recent">Most recent first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Manager</Label>
            <Select value={managerFilterId} onValueChange={(v) => { setManagerFilterId(v); setMemberId("all"); }}>
              <SelectTrigger data-testid="plan-manager-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All managers</SelectItem>
                {managerList.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`plan-manager-option-${m.id}`}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Team member</Label>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="plan-member-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                {memberOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`plan-member-option-${m.id}`}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : processed.length === 0 ? (
        <Card className="p-12 text-center rounded-xl" data-testid="team-plans-empty">
          <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No open plan items for this selection.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="team-plans-list">
          {processed.map((g) => (
            <Card key={g.user_id} className="p-5 rounded-xl" data-testid={`team-plan-group-${g.user_id}`}>
              <div className="flex items-center gap-3 mb-4">
                <Avatar src={g.avatar} name={g.user_name} className="h-9 w-9 text-sm" testid={`plan-avatar-${g.user_id}`} />
                <p className="font-heading font-semibold">{g.user_name}</p>
                <Badge className="ml-auto rounded-full border-0 bg-foreground/10 text-foreground text-xs">
                  {g.items.length}
                </Badge>
              </div>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <li key={it.id} className="flex items-start gap-2 text-sm" data-testid={`team-plan-item-${it.id}`}>
                    <Circle className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                    <span className="flex-1 break-words text-foreground/90">{it.text}</span>
                    <PendingBadge date={it.date} />
                    <span className="text-[11px] font-mono text-muted-foreground shrink-0">{it.date}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
